import { useState } from 'react'

import { Segmented } from '@/components/ui'
import { NeedsConnection } from '@/components/pos/office/NeedsConnection'
import { OfficeTable } from '@/components/pos/office/OfficeTable'
import { Pager } from '@/components/pos/office/Pager'
import { useSession } from '@/contexts/SessionContext'
import { useProducts } from '@/hooks/features/catalog'
import { useOfficeQuery, useOnline } from '@/hooks/features/office'
import { OfficeModel } from '@/http/models'

type Status = 'expiring' | 'expired' | 'all'

/** This branch's batches that are expiring or expired (`/stock/expiry`, `stocks:expiry`). */
export const ExpiryList = () => {
  const { device } = useSession()
  const online = useOnline()
  const [status, setStatus] = useState<Status>('expiring')
  const [page, setPage] = useState(1)
  const { data: products } = useProducts({})
  const { data, isFetching } = useOfficeQuery(['expiry', status, page], () => OfficeModel.expiry({ branch_id: device!.branch_id, status, page }))
  if (!online) return <NeedsConnection />
  const nameOf = (id: unknown) => products?.items.find((p) => p.server_id === String(id) || p._id === String(id))?.name ?? String(id)

  return (
    <div className="min-h-0 flex-1 space-y-3 overflow-auto px-4 pb-4 md:px-6">
      <Segmented<Status>
        value={status}
        onChange={(s) => (setStatus(s), setPage(1))}
        options={[
          { value: 'expiring', label: 'Expiring' },
          { value: 'expired', label: 'Expired' },
          { value: 'all', label: 'All with a date' },
        ]}
      />
      <OfficeTable
        cards
        loading={isFetching}
        rows={data?.result ?? []}
        rowKey={(r) => String(r._id)}
        empty="No batches here."
        columns={[
          { label: 'Product', cell: (r) => <span className="font-medium text-ink">{nameOf(r.product_variation_id)}</span> },
          { label: 'Expires', cell: (r) => (r.expiry_date ? String(r.expiry_date).slice(0, 10) : '') },
          { label: 'Quantity', className: 'text-right tabular-nums', cell: (r) => Number(r.quantity ?? 0) },
        ]}
      />
      <Pager page={page} count={data?.count ?? 0} onChange={setPage} />
    </div>
  )
}
